import { createSelector } from '@reduxjs/toolkit';

import { isFeatureAvailable } from 'cadenza/features';
import type { RootState, RootStateOr } from 'cadenza/store/cadenza-store';
import { getProjectId } from 'cadenza/store/cadenza.state';
import type { VersionTagAttributes } from 'cadenza/workbook/header/workbook-header';
import {
  getActiveWorksheetId,
  getWorkbookPublicationStage,
  getWorkbookRelation,
  getWorkbookType,
  getWorkbookVersion,
  getWorksheets,
  hasBoundObjecttypes,
  isRepositoryPublishingProcessEnabled,
  isWorkbookChanged,
  isWorkbookSaved,
  isWorkbookSharable,
  isWorkbookVisibilityRestricted,
  isWorkbookWritable,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import { isWorksheetVisible } from 'cadenza/workbook/worksheet/worksheet-util';

export function getVersionTagAttributes(state: RootStateOr<WorkbookState>): VersionTagAttributes {
  return {
    workbookType: getWorkbookType(state),
    workbookVersion: getWorkbookVersion(state),
    workbookVersionMode: getWorkbookPublicationStage(state) ?? (isWorkbookSaved(state) ? 'saved' : 'unsaved'),
    workbookRelation: getWorkbookRelation(state),
    visible:
      isWorkbookWritable(state) || (getWorkbookType(state) === 'SNAPSHOT' && isWorkbookVisibilityRestricted(state)),
    disabled: !isWorkbookFeatureAvailable(state, 'workbook-select-version'),
    modified: isWorkbookChanged(state),
  };
}

export function isAccessSettingsButtonVisible(state: RootState) {
  return (
    isWorkbookFeatureAvailable(state, 'workbook-access-settings') &&
    isWorkbookSaved(state) &&
    isWorkbookSharable(state) &&
    !getProjectId(state)
  ); // For project workbooks access settings come from the project.
}

export function isSaveButtonVisible(state: RootStateOr<WorkbookState>) {
  return (
    isWorkbookFeatureAvailable(state, 'workbook-save') &&
    canTheUserSaveWorkbooks(state) &&
    isWorkbookWritable(state) &&
    isWorkbookSaved(state)
  );
}

export function isSaveAsButtonVisible(state: RootStateOr<WorkbookState>) {
  return (
    isWorkbookFeatureAvailable(state, 'workbook-save') &&
    canTheUserSaveWorkbooks(state) &&
    (!isWorkbookWritable(state) || !isWorkbookSaved(state))
  );
}

export function isWorkbookSaveable(state: RootStateOr<WorkbookState>) {
  return isSaveButtonVisible(state) || isSaveAsButtonVisible(state);
}

export function isSaveAndPublishButtonVisible(state: RootStateOr<WorkbookState>) {
  return isSaveButtonVisible(state) && isRepositoryPublishingProcessEnabled(state);
}

export function canTheUserSaveWorkbooks(state: RootStateOr<WorkbookState>) {
  const canSaveIntoARepository =
    hasBoundObjecttypes(state) ?
      canTheUserSaveIntoDatabaseRepositoryWithCmdb()
    : canTheUserSaveIntoDatabaseRepository();
  return canSaveIntoARepository && isWorkbookFeatureAvailable(state, 'workbook-management');
}

/**
 * Returns the worksheets that are visible to the current user and that they can navigate to.
 * Worksheets with the hidden setting set to true are included in this selector only if the user has 'write' privileges on the workbook.
 */
export const getAccessibleWorksheets = createSelector(
  [getActiveWorksheetId, getWorksheets, isWorkbookWritable],
  (activeWorksheetId, worksheets, includeHiddenWorksheets) => {
    return includeHiddenWorksheets ? worksheets : (
        worksheets.filter((worksheet) => worksheet.id === activeWorksheetId || isWorksheetVisible(worksheet))
      );
  },
);

function canTheUserSaveIntoDatabaseRepositoryWithCmdb() {
  return isFeatureAvailable('WRITABLE_DATABASE_REPOSITORY_WITH_CMDB_AND_WRITABLE_FOLDER');
}

function canTheUserSaveIntoDatabaseRepository() {
  return isFeatureAvailable('WRITABLE_DATABASE_REPOSITORY_WITH_WRITABLE_FOLDER');
}
